import { useMemo, useContext } from 'react';
import { signDocTypeConfig, SignDocTypes, SignDocsAssingOrganizationInUpdate } from '../Components/SignDoc/SignDocLib';
import { I18nReactContext } from '../contexts/I18nContext';

/**
 * Custom hook for accessing sign document type configurations with translations
 * @returns {Object} Object containing sign doc type configurations and helper methods
 */
const useSignDocTypes = () => {
    const { i18n } = useContext(I18nReactContext);
    
    return useMemo(() => {
        // Create a deep copy of the configurations, but be careful with React elements
        const configs = {};
        
        // Special handling for nested objects with potential React elements
        Object.entries(signDocTypeConfig).forEach(([type, config]) => {
            configs[type] = { ...config };
        });
        
        
        /**
         * Get a nested property's translation from a sign doc type configuration
         * 
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type
         * @param {string} params.section - The section to access
         * @param {string} params.property - The translation key property to retrieve
         * @param {string} params.fallbackKey - Fallback translation key pattern
         * @param {string | import('react').ReactNode} params.defaultValue - Default value if no translation found
         * @returns {string | import('react').ReactNode} The translated text
         */
        const getNestedTranslation = ({ type, section, property, fallbackKey = null, defaultValue = '' }) => {
            const config = configs[type];
            
            // Check if we have a specific key defined in config
            if (config && config[section] && config[section][property]) {
                return i18n.translate(config[section][property]);
            }
            
            // If fallback key pattern is provided, try it
            if (fallbackKey) {
                const dynamicKey = fallbackKey.replace('{type}', type);
                try {
                    // Attempt to translate the dynamic key
                    const translation = i18n.translate(dynamicKey);
                    
                    // Check if translation exists by seeing if it returns the key itself
                    if (translation && translation !== dynamicKey) {
                        return translation;
                    }
                } catch (e) {
                    // Silently catch errors if translation fails
                }
            }
            
            // Return default if nothing else works
            return defaultValue;
        };

        /**
         * Get configuration for a specific sign doc type
         * 
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type (use SignDocTypes enum)
         * @returns {Object} Configuration object for the specified type
         */
        const getConfig = ({ type }) => {
            return configs[type] || null;
        };
        
        /**
         * Get a nested property from a sign doc type configuration
         * 
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type (use SignDocTypes enum)
         * @param {string} params.section - The section to access (e.g., 'honeyCombModal', 'createEditNewSignDocModal')
         * @param {string} params.property - The property name to retrieve
         * @param {any} params.defaultValue - Default value to return if property is not found
         * @returns {any} The property value or default value
         */
        const getNestedProperty = ({ type, section, property, defaultValue = null }) => {
            const config = configs[type];
            if (!config || !config[section] || !(property in config[section])) {
                return defaultValue;
            }
            return config[section][property];
        };
        
        /**
         * Get a specific property from a sign doc type configuration
         * 
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type (use SignDocTypes enum)
         * @param {string} params.property - The property name to retrieve
         * @param {any} params.defaultValue - Default value to return if property is not found
         * @returns {any} The property value or default value
         */
        const getConfigProperty = ({ type, property, defaultValue = null }) => {
            const config = configs[type];
            return config && property in config ? config[property] : defaultValue;
        };
        
        /**
         * Get the honeycomb text with translation
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type
         * @returns {string|React.ReactNode} The translated honeycomb text (can be string or React node with line breaks)
         */
        const getHoneycombText = ({ type }) => {
            const result = getNestedTranslation({
                type,
                section: 'honeyCombModal',
                property: 'honeyCombTextKey',
                fallbackKey: 'HoneycombTasks.servicesText.{type}',
                defaultValue: ''
            });
            return result;
        };
        
        /**
         * Get honeycomb icon, safely handling different icon formats
         */
        const getHoneycombIcon = ({ type }) => {
            const iconValue = getNestedProperty({ 
                type, 
                section: 'honeyCombModal', 
                property: 'honeyCombImageIcon', 
                defaultValue: null 
            });
            
            // No icon defined
            if (!iconValue) {
                return null;
            }
            
            // Return the icon value directly, let the rendering component handle different types
            return iconValue;
        };
        
        /**
         * Check if there's a secondary icon
         */
        const getHoneycombSecondaryIcon = ({ type }) => {
            return getNestedProperty({ 
                type, 
                section: 'honeyCombModal', 
                property: 'isHoneyCombSecondaryImageIcon', 
                defaultValue: false 
            });
        };
        
        /**
         * Check if AI is implemented for this type
         */
        const getIsAIImplemented = ({ type }) => {
            return getNestedProperty({ 
                type, 
                section: 'honeyCombModal', 
                property: 'isAIImplemented', 
                defaultValue: false 
            });
        };
        
        /**
         * Get honeycomb column position
         */
        const getHoneycombColumnPosition = ({ type }) => {
            return getNestedProperty({
                type,
                section: 'honeyCombModal',
                property: 'columnPosition',
                defaultValue: undefined
            });
        };

        /**
         * Get honeycomb row position
         */
        const getHoneycombRowPosition = ({ type }) => {
            return getNestedProperty({
                type,
                section: 'honeyCombModal',
                property: 'rowPosition',
                defaultValue: undefined
            });
        };

        /**
         * Get management image icon
         */
        const getMgmntImageIcon = ({ type }) => {
            return getConfigProperty({ 
                type, 
                property: 'mgmntImageIcon', 
                defaultValue: '' 
            });
        };
        
        /**
         * Get valid SignDoc types for this type
         */
        const getSignDocOptionsTypesForCreateEditSignDocModal = ({ type }) => {
            return getConfigProperty({
                type,
                property: 'signDocOptionsTypes',
                defaultValue: [type]
            });
        };

        /**
         * Get if file upload is required
         */
        const getRequiresFileUpload = ({ type }) => {
            return getNestedProperty({ 
                type, 
                section: 'createEditNewSignDocModal', 
                property: 'requiresFileUpload', 
                defaultValue: false 
            });
        };
        
        /**
         * Get if link is required
         */
        const getRequiresLink = ({ type }) => {
            return getNestedProperty({ 
                type, 
                section: 'createEditNewSignDocModal', 
                property: 'requiresLink', 
                defaultValue: false 
            });
        };
        
        /**
         * Get file upload configuration
         */
        const getFileUploadConfig = ({ type }) => {
            return getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'fileUploadConfig',
                defaultValue: {
                    maxSize: 50000000, // 50MB default
                    accept: {
                        'application/pdf': ['.pdf']
                    },
                    supportedFileTypesForNotifications: ['PDF']
                }
            });
        };

        /**
         * Get if organization chips should be shown on creation.
         * Derived from SignDocsAssingOrganizationInUpdate — types in that map
         * assign organizations later, so they don't show org chips on creation.
         */
        const getShouldShowOrganizationChips = ({ type }) => {
            return !SignDocsAssingOrganizationInUpdate[type];
        };
        
        /**
         * Get the create button text
         */
        const getCreateButtonText = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'createEditNewSignDocModal',
                property: 'createButtonTextKey',
                fallbackKey: 'newDocSign.modal.createButtonText.{type}',
                defaultValue: i18n.translate('general.create')
            });
        };
        
        /**
         * Get the create button text key (non-translated)
         */
        const getCreateButtonTextKey = ({ type }) => {
            return getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'createButtonTextKey',
                defaultValue: 'general.create'
            });
        };
        
        /**
         * Get the extra save button text
         */
        const getExtraSaveButtonText = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'createEditNewSignDocModal',
                property: 'extraSaveButtonTextKey',
                fallbackKey: 'newDocSign.modal.{type}ExtraSaveButton',
                defaultValue: null
            });
        };
        
        /**
         * Get the extra save button text key (non-translated)
         */
        const getExtraSaveButtonTextKey = ({ type }) => {
            return getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'extraSaveButtonTextKey',
                defaultValue: null
            });
        };
        
        /**
         * Get the label for the document name field
         */
        const getDocNameLabel = ({ type }) => {
            // First try to get from a nested property if it exists
            const labelKey = getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'docNameLabelKey',
                defaultValue: null
            });
            
            // If exists, translate and return
            if (labelKey) return i18n.translate(labelKey);
            
            // Otherwise, use the dynamic label fallback
            return i18n.translate(`newDocSign.modal.docNameLabel.${type}`);
        };
        
        /**
         * Get the label for the document description field
         */
        const getDocDescriptionLabel = ({ type }) => {
            // First try to get from a nested property if it exists
            const labelKey = getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'docDescriptionLabelKey',
                defaultValue: null
            });
            
            // If exists, translate and return
            if (labelKey) return i18n.translate(labelKey);
            
            // Otherwise, use the dynamic label fallback
            return i18n.translate(`newDocSign.modal.docDescriptionLabel.${type}`);
        };
        
        /**
         * Get the text for the document drop zone
         */
        const getDocDropLabel = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'createEditNewSignDocModal',
                property: 'dropDocLabelKey',
                fallbackKey: `newDocSign.modal.dropDocLabel.${type}`,
                defaultValue: i18n.translate('newDocSign.modal.dropSignDocHere')
            });
        };
        
        /**
         * Get the learning management sub path
         */
        const getLearningManagementSubPath = ({ type }) => {
            return getConfigProperty({
                type,
                property: 'learningManagementSubPath',
                defaultValue: null
            });
        };
        
        /**
         * Check if this doc type should show the allow fast forward checkbox
         */
        const getShouldShowAllowFastForwardCheckbox = ({ type }) => {
            return getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'shouldShowAllowFastForwardCheckbox',
                defaultValue: false
            });
        };

        /**
         * Get redirect URL for creating a new item (dynamic URL generator)
         */
        const getRedirectUrlOnCreate = ({ type, signDocId }) => {
            const generator = getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'redirectUrlOnCreate',
                defaultValue: null
            });
            return generator && signDocId ? generator(signDocId) : null;
        };
        
        /**
         * Get redirect URL for editing an item (dynamic URL generator)
         */
        const getRedirectUrlOnEdit = ({ type, signDocId }) => {
            const generator = getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'redirectUrlOnEdit',
                defaultValue: null
            });
            return generator && signDocId ? generator(signDocId) : null;
        };
        
        /**
         * Determine if this doc type should show the extra save button in edit mode
         */
        const getShouldShowExtraSaveButton = ({ type }) => {
            const extraSaveButtonTextKey = getNestedProperty({
                type,
                section: 'createEditNewSignDocModal',
                property: 'extraSaveButtonTextKey',
                defaultValue: null
            });
            
            return extraSaveButtonTextKey !== null;
        };
        
        /**
         * Get MySignDocs title for todo items
         */
        const getMySignDocsTodoTitle = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'mySignDocs',
                property: 'firstTitleKey',
                fallbackKey: 'learningCenter.{type}ToDo',
                defaultValue: i18n.translate('learningCenter.signDocsToDo')
            });
        };
        
        /**
         * Get MySignDocs title for completed items
         */
        const getMySignDocsCompletedTitle = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'mySignDocs',
                property: 'secondTitleKey',
                fallbackKey: 'learningCenter.{type}Completed',
                defaultValue: i18n.translate('learningCenter.signDocsCompleted')
            });
        };
        
        /**
         * Get MySignDocs empty state text for todo items
         */
        const getMySignDocsTodoEmptyText = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'mySignDocs',
                property: 'emptyStateTextsTodo',
                fallbackKey: 'learningCenter.no{type}ToDo',
                defaultValue: i18n.translate('learningCenter.noSignDocsToDo')
            });
        };
        
        /**
         * Get MySignDocs empty state text for completed items
         */
        const getMySignDocsCompletedEmptyText = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'mySignDocs',
                property: 'emptyStateTextsCompleted',
                fallbackKey: 'learningCenter.no{type}Completed',
                defaultValue: i18n.translate('learningCenter.noSignDocsCompleted')
            });
        };
        
        /**
         * Check if a SignDoc type supports admin preview
         *
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type (use SignDocTypes enum)
         * @returns {boolean} Whether the SignDoc type is previewable
         */
        const getIsPreviewable = ({ type }) => {
            return getConfigProperty({
                type,
                property: 'isPreviewable',
                defaultValue: false
            });
        };

        /**
         * Get the preview mode for a SignDoc type
         *
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type (use SignDocTypes enum)
         * @returns {string|null} The preview mode identifier, or null if not previewable
         */
        const getPreviewMode = ({ type }) => {
            return getConfigProperty({
                type,
                property: 'previewMode',
                defaultValue: null
            });
        };

        /**
         * Get the navigation path for a SignDoc type
         *
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type
         * @param {string} params.id - The SignDoc ID
         * @returns {string} The navigation path
         */
        const getSignDocLinkPath = ({ type, id }) => {
            const config = configs[type];
            if (config && config.getSignDocLinkPath) {
                return config.getSignDocLinkPath({ id });
            }
            // Fallback to general signDoc path
            return `/signDoc/${id}`;
        };

        /**
         * Get AI generation modal title for a SignDoc type
         *
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type
         * @returns {string} The translated AI generation title (e.g., "Create Exam with AI")
         */
        const getAIGenerationTitle = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'aiGeneration',
                property: 'titleKey',
                fallbackKey: 'AIGeneration.titles.{type}',
                defaultValue: i18n.translate('AIGeneration.titles.default')
            });
        };

        /**
         * Get AI generation button text for a SignDoc type
         *
         * @param {object} params - Parameters
         * @param {string} params.type - The sign doc type
         * @returns {string} The translated button text (e.g., "Generate Questions with AI")
         */
        const getAIGenerationButtonText = ({ type }) => {
            return getNestedTranslation({
                type,
                section: 'aiGeneration',
                property: 'buttonTextKey',
                fallbackKey: 'AIGeneration.buttonText.{type}',
                defaultValue: i18n.translate('AIGeneration.buttonText.default')
            });
        };


        // Return both the raw types and helper methods
        return {
            signDocTypes: Object.keys(SignDocTypes),
            signDocTypeConfigs: configs,
            
            // Core config access methods
            getConfig,
            getConfigProperty,
            getNestedProperty,
            getNestedTranslation,
            
            // Individual property accessors - Honeycomb
            getHoneycombText,
            getHoneycombIcon,
            getHoneycombSecondaryIcon,
            getIsAIImplemented,
            getHoneycombColumnPosition,
            getHoneycombRowPosition,

            // Individual property accessors - General
            getMgmntImageIcon,
            getSignDocOptionsTypesForCreateEditSignDocModal,
            getLearningManagementSubPath,
            getSignDocLinkPath,
            getIsPreviewable,
            getPreviewMode,

            // Individual property accessors - CreateEditNewSignDocModal
            getRequiresFileUpload,
            getRequiresLink,
            getFileUploadConfig,
            getShouldShowOrganizationChips,
            getCreateButtonText,
            getCreateButtonTextKey,
            getExtraSaveButtonText,
            getExtraSaveButtonTextKey,
            getDocNameLabel,
            getDocDescriptionLabel, 
            getDocDropLabel,
            getShouldShowExtraSaveButton,
            getShouldShowAllowFastForwardCheckbox,
            getRedirectUrlOnCreate,
            getRedirectUrlOnEdit,
            
            // Individual property accessors - MySignDocs
            getMySignDocsTodoTitle,
            getMySignDocsCompletedTitle,
            getMySignDocsTodoEmptyText,
            getMySignDocsCompletedEmptyText,

            // Individual property accessors - AI Generation
            getAIGenerationTitle,
            getAIGenerationButtonText,
        };
    }, [i18n]);
};

export default useSignDocTypes; 